import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRight } from "lucide-react";

const EMPTY_FORM = {
  property_name: "", contact_name: "", email: "", phone: "",
  property_count: "", country: "", message: "", consent_to_contact: true,
};

/**
 * Founding Property Program lead-capture section.
 * Posts to public POST /api/founding-leads (rate-limited 5/min).
 */
export default function LandingFoundingProgram() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_to_contact) { setError("Please tick the consent box."); return; }
    setBusy(true); setError("");
    try {
      const payload = {
        ...form,
        property_count: form.property_count ? parseInt(form.property_count, 10) : null,
      };
      await api.post("/founding-leads", payload);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Submission failed. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <section id="resources" className="py-20 border-b border-zinc-200 bg-stone-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">Founding Property Program</div>
          <h2 className="font-display font-black tracking-tight text-4xl lg:text-5xl mt-3">Help shape the network</h2>
          <p className="mt-4 text-zinc-700 max-w-xl">
            Be among the first hospitality businesses shaping the future of hospitality intelligence.
            Founding properties receive concierge onboarding, lifetime discounts, and a seat on our policy review board.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-zinc-700">
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Lifetime founding-member pricing</li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Direct line to the product team</li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Seat on the Incident Review policy board</li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> White-glove onboarding &amp; data migration</li>
          </ul>
        </div>

        <div className="bg-white border border-zinc-200 rounded-lg p-6 shadow-sm" data-testid="founding-form-card">
          {done ? (
            <div className="text-center py-8" data-testid="founding-form-done">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center"><CheckCircle2 className="w-7 h-7" /></div>
              <h3 className="font-display font-black text-2xl mt-3">Thanks — we&apos;ll be in touch.</h3>
              <p className="text-sm text-zinc-600 mt-2 max-w-prose">Our founding-property team responds within two business days. Watch for an email from <strong>contact@guestintel.net</strong>.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="founding-form">
              <h3 className="font-display font-bold text-xl">Apply as a Founding Property</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">Property / group name</label>
                  <input required value={form.property_name} onChange={upd("property_name")} className="w-full h-10 border border-zinc-300 rounded-md px-3 text-sm mt-1" data-testid="founding-input-property" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">Your name</label>
                  <input required value={form.contact_name} onChange={upd("contact_name")} className="w-full h-10 border border-zinc-300 rounded-md px-3 text-sm mt-1" data-testid="founding-input-name" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">Email</label>
                  <input required type="email" value={form.email} onChange={upd("email")} className="w-full h-10 border border-zinc-300 rounded-md px-3 text-sm mt-1" data-testid="founding-input-email" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">Phone (optional)</label>
                  <input value={form.phone} onChange={upd("phone")} className="w-full h-10 border border-zinc-300 rounded-md px-3 text-sm mt-1" data-testid="founding-input-phone" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">Country</label>
                  <input value={form.country} onChange={upd("country")} className="w-full h-10 border border-zinc-300 rounded-md px-3 text-sm mt-1" data-testid="founding-input-country" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500"># Properties</label>
                  <input type="number" min={1} value={form.property_count} onChange={upd("property_count")} className="w-full h-10 border border-zinc-300 rounded-md px-3 text-sm mt-1" data-testid="founding-input-count" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500">Tell us about your operation (optional)</label>
                <textarea rows={3} value={form.message} onChange={upd("message")} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm mt-1" data-testid="founding-input-message" />
              </div>
              <label className="flex items-start gap-2 text-xs text-zinc-600 cursor-pointer">
                <input type="checkbox" checked={form.consent_to_contact} onChange={(e) => setForm({ ...form, consent_to_contact: e.target.checked })} className="mt-0.5" data-testid="founding-input-consent" />
                <span>I&apos;m happy for GuestIntel to contact me about joining the Founding Property Program. We never sell your data.</span>
              </label>
              {error && <div className="text-sm text-rose-600" data-testid="founding-form-error">{error}</div>}
              <Button type="submit" disabled={busy} className="rounded-full w-full" data-testid="founding-submit-btn">
                {busy ? "Sending…" : "Apply as a Founding Property"} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <p className="text-[11px] text-zinc-500">Already on the platform? <a href={`${process.env.REACT_APP_APP_URL || ""}/login`} target="_blank" rel="noopener noreferrer" className="underline">Sign in</a>.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
